'use client';

import { useTranslations } from 'next-intl';
import { QRCodeSVG } from 'qrcode.react';
import { forwardRef } from 'react';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { UiImage } from '@/lib/ui-kit/ui-image';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';

export interface SharePositionCardProps {
  eventTitle: string;
  eventImage: string;
  outcome: 'Yes' | 'No';
  /** Outcome name for multi-outcome events (e.g. "Cleveland Cavaliers") */
  outcomeName?: string;
  /** Buy/Sell side — open positions are always bought, so this defaults to 'Buy' */
  side?: 'Buy' | 'Sell';
  entryPrice: number;
  /** Exit price (sell price, or 100¢/0¢ settlement) — omitted while the position is still open */
  exitPrice?: number;
  myBet: number;
  possibleWin: number;
  /** Realized PnL — shown instead of "Possible Win" when side is Sell */
  pnl?: number;
  /** Realized PnL % — shown in brackets next to PnL for an unresolved Sell */
  pnlPercent?: number;
  /** Outcome already resolved — show actual result (Won/Lost) instead of "Possible Win" */
  isResolved?: boolean;
  /** Position won after resolution — controls Won vs Lost display */
  isWon?: boolean;
  /** Settlement (auto-redemption, not a trade) — hides "My Position", shows only Won/Lost */
  isSettlement?: boolean;
  referralCode: string;
  referralLink: string;
  isLoading?: boolean;
}

const labelStyle: React.CSSProperties = {
  color: '#74797F',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '-0.13px',
};

const valueStyle: React.CSSProperties = {
  color: '#0E1011',
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: '19px',
};

/**
 * SharePositionCard - A card component for sharing position as image.
 *
 * IMPORTANT: This component uses inline styles instead of Tailwind classes
 * because html2canvas doesn't support oklch() color function used in Tailwind v4.
 * All colors must be in hex/rgb/rgba format for proper image export.
 */
export const SharePositionCard = forwardRef<HTMLDivElement, SharePositionCardProps>(
  (
    {
      eventTitle,
      eventImage,
      outcome,
      outcomeName,
      side = 'Buy',
      entryPrice,
      exitPrice,
      myBet,
      possibleWin,
      pnl,
      pnlPercent,
      isResolved,
      isWon,
      isSettlement,
      referralCode,
      referralLink,
      isLoading,
    },
    ref,
  ) => {
    const t = useTranslations('sharePosition');

    const formatSignedPercent = (value: number) =>
      `${formatMoney(value, { decimals: 2, signed: true })}%`;

    // Resolved (settled, whether via a late sale or auto-redemption) shows the actual
    // Won/Lost result — but isWon is a tri-state: undefined means a draw or an outcome
    // we don't actually know yet, and must NOT default to "Lost". Falls back to generic
    // PnL wording in that case; an unresolved Sell also shows generic PnL.
    const secondaryLabel =
      isResolved && isWon !== undefined
        ? isWon
          ? 'won'
          : 'lost'
        : side === 'Sell' || isResolved
          ? 'pnl'
          : 'possibleWin';
    // Net gain/loss (payout - stake), not the raw payout — prefer the backend's own
    // pnl figure; the subtraction is only a fallback for callers that don't pass one,
    // and only when we actually know the outcome (isWon undefined → no fallback guess).
    const resolvedPnlFallback =
      isWon === undefined ? 0 : isWon ? possibleWin - myBet : myBet > 0 ? -myBet : 0;
    const secondaryValue = isResolved
      ? (pnl ?? resolvedPnlFallback)
      : side === 'Sell'
        ? (pnl ?? 0)
        : possibleWin;
    // No percent once it's a Won/Lost result — only an unresolved Sell's plain PnL gets one
    const secondarySuffix =
      !isResolved && side === 'Sell' && pnlPercent !== undefined
        ? ` (${formatSignedPercent(pnlPercent)})`
        : '';

    // Show name only for multi-outcome events (skip bare Yes/No or event-title duplicates)
    const resolvedName =
      outcomeName && outcomeName !== 'Yes' && outcomeName !== 'No' && outcomeName !== eventTitle
        ? outcomeName
        : undefined;

    return (
      <div
        ref={ref}
        style={{
          position: 'relative',
          width: '352px',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #00205D 0%, #000A1C 100%)',
        }}
      >
        {/* Background image with blur */}
        <div
          style={{
            position: 'absolute',
            inset: '-20px',
            background: 'url(/share-card-bg.png) #d1d5db 50% / cover no-repeat',
            filter: 'blur(12.27px)',
          }}
        />

        {/* Content */}
        <div
          style={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: '20px 16px',
          }}
        >
          {/* Header with OUTPOLL logo */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* biome-ignore lint/performance/noImgElement: screenshot card — next/image lazy-loads and breaks html2canvas */}
            <img
              src="/share-card-logo.svg"
              alt="OUTPOLL"
              width={88}
              height={18}
              style={{ width: '88px', height: '18px' }}
            />
          </div>

          {/* Main white card */}
          <div
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(13.75px)',
              WebkitBackdropFilter: 'blur(13.75px)',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            {/* Event info section */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 0',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '7px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: '#e5e7eb',
                }}
              >
                <UiImage
                  src={eventImage || '/evfallback.webp'}
                  alt={eventTitle}
                  size="sm"
                  fallback="/evfallback.webp"
                />
              </div>

              <p
                style={{
                  ...labelStyle,
                  color: '#0E1011',
                  margin: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {eventTitle}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Bought / Avg row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                  <span style={labelStyle}>{t(side === 'Sell' ? 'sold' : 'bought')}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {resolvedName && (
                      <span
                        style={{
                          ...valueStyle,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minWidth: 0,
                        }}
                      >
                        {resolvedName}
                      </span>
                    )}
                    <span
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
                    >
                      {resolvedName && (
                        <span
                          style={{
                            width: '3px',
                            height: '3px',
                            borderRadius: '50%',
                            backgroundColor: '#74797F',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <span
                        style={{
                          ...valueStyle,
                          color: outcome === 'Yes' ? '#01B257' : '#FB4E4B',
                        }}
                      >
                        {outcome}
                      </span>
                    </span>
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    alignItems: 'flex-end',
                    flexShrink: 0,
                  }}
                >
                  <span style={labelStyle}>
                    {exitPrice !== undefined ? t('entryExit') : t('entry')}
                  </span>
                  <span style={valueStyle}>
                    {exitPrice !== undefined
                      ? `${formatCents(entryPrice)} → ${formatCents(exitPrice)}`
                      : formatCents(entryPrice)}
                  </span>
                </div>
              </div>

              {/* Dashed separator with OUTPOLL icon */}
              {/* biome-ignore lint/performance/noImgElement: screenshot card — next/image lazy-loads and breaks html2canvas */}
              <img
                src="/share-card-divider.svg"
                alt=""
                width={321}
                height={28}
                style={{ width: '100%', height: 'auto' }}
              />

              {/* My Position / Possible Win row — Settlement has no trade to show a position for */}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {!isSettlement && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={labelStyle}>{t('myBet')}</span>
                    <span style={valueStyle}>{formatMoney(myBet, { prefix: '$' })}</span>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    alignItems: isSettlement ? 'flex-start' : 'flex-end',
                  }}
                >
                  <span style={labelStyle}>{t(secondaryLabel)}</span>
                  <span
                    style={{
                      ...valueStyle,
                      color: secondaryValue < 0 ? '#FB4E4B' : '#01B257',
                    }}
                  >
                    {formatMoney(secondaryValue, { prefix: '$' })}
                    {secondarySuffix}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Referral section */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              padding: '12px 0',
              borderTop: '1px solid #3B4447',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
              {isLoading ? (
                <Skeleton className="h-[19px] w-[210px] bg-white/40" />
              ) : (
                <p
                  style={{
                    ...valueStyle,
                    margin: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('referralCode')} {referralCode || 'YOURCODE123'}
                </p>
              )}
              <p
                style={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: '#0E1011',
                  margin: 0,
                }}
              >
                {t('referralDescription')}
              </p>
            </div>

            {/* QR Code */}
            <div
              style={{
                flexShrink: 0,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                padding: '4px',
              }}
            >
              <QRCodeSVG
                value={referralLink || 'https://outpoll.com'}
                size={46}
                level="M"
                bgColor="#ffffff"
                fgColor="#1a1a1a"
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

SharePositionCard.displayName = 'SharePositionCard';
